import { useRef } from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from 'framer-motion';
import { Zap, CheckCircle2, TrendingUp } from 'lucide-react';

const talentProfiles = [
  {
    initials: 'KF',
    name: 'Khalid F.',
    role: 'Senior SEO Specialist',
    bgStyle: { background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' },
    textColor: '#1e40af',
  },
  {
    initials: 'DL',
    name: 'Dina L.',
    role: 'Content Strategist',
    bgStyle: { background: 'linear-gradient(135deg, #fce7f3, #fbcfe8)' },
    textColor: '#9d174d',
  },
  {
    initials: 'RB',
    name: 'Raj B.',
    role: 'Full-Stack Engineer',
    bgStyle: { background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)' },
    textColor: '#065f46',
  },
];

// Wraps a card in: outer (parallax x/y) + inner (idle float y)
// These two transforms stack cleanly because they live on separate DOM elements.
const ParallaxCard = ({
  children,
  px,        // parallax x motion value
  py,        // parallax y motion value
  floatAmt = 8,
  floatDuration = 5,
  floatDelay = 0,
  style = {},
  zIndex = 10,
}) => (
  <motion.div
    style={{
      position: 'absolute',
      zIndex,
      x: px,
      y: py,
      ...style,
    }}
  >
    <motion.div
      animate={{ y: [0, -floatAmt, 0] }}
      transition={{
        duration: floatDuration,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: floatDelay,
        repeatType: 'loop',
      }}
    >
      {children}
    </motion.div>
  </motion.div>
);

const TalentCard = ({ profile }) => (
  <div className="talent-card">
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-11 h-11 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-[13px]"
        style={{ ...profile.bgStyle, color: profile.textColor }}
      >
        {profile.initials}
      </div>
      <div>
        <p className="font-semibold text-[14px] text-foreground leading-tight">{profile.name}</p>
        <p className="text-[12px] text-muted-foreground leading-tight mt-0.5">{profile.role}</p>
      </div>
    </div>
    <div className="flex items-center justify-between gap-2">
      <span className="ai-badge">
        <Zap size={10} className="fill-current" />
        AI Enhanced
      </span>
      <span className="available-badge">
        <span
          className="w-1.5 h-1.5 rounded-full inline-block"
          style={{ background: 'hsl(142 72% 42%)' }}
        />
        Available
      </span>
    </div>
  </div>
);

const TalentCardCluster = () => {
  const containerRef = useRef(null);

  // Raw normalised mouse position (-1 → +1)
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);

  // Spring smoothing — feels physical
  const springCfg = { stiffness: 80, damping: 22, mass: 0.6 };
  const sX = useSpring(rawX, springCfg);
  const sY = useSpring(rawY, springCfg);

  // Each element has its own depth multiplier.
  // Positive = moves with mouse, Negative = moves against (deeper parallax layer feel).
  const chip10xX  = useTransform(sX, v => v * 28);
  const chip10xY  = useTransform(sY, v => v * 20);

  const chipWLX   = useTransform(sX, v => v * -14);
  const chipWLY   = useTransform(sY, v => v * 22);

  const cardKFX   = useTransform(sX, v => v * 22);
  const cardKFY   = useTransform(sY, v => v * 16);

  const cardDLX   = useTransform(sX, v => v * -16);
  const cardDLY   = useTransform(sY, v => v * -18);

  const cardRBX   = useTransform(sX, v => v * 18);
  const cardRBY   = useTransform(sY, v => v * -14);

  const chip48hX  = useTransform(sX, v => v * -22);
  const chip48hY  = useTransform(sY, v => v * 16);

  const chipMgX   = useTransform(sX, v => v * 20);
  const chipMgY   = useTransform(sY, v => v * -20);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    rawX.set((e.clientX - rect.left  - rect.width  / 2) / (rect.width  / 2));
    rawY.set((e.clientY - rect.top   - rect.height / 2) / (rect.height / 2));
  };

  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[440px]"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >

      {/* ── 10x Faster Output chip ── */}
      <ParallaxCard
        px={chip10xX} py={chip10xY}
        style={{ top: '16px', left: '16px', minWidth: '136px' }}
        floatAmt={7} floatDuration={3.8} floatDelay={0}
      >
        <div className="talent-card">
          <div
            className="text-[34px] font-black leading-none tracking-tightest"
            style={{ color: 'hsl(var(--apple-blue))' }}
          >
            10x
          </div>
          <div className="text-[12px] text-muted-foreground mt-1 font-medium">Faster Output</div>
        </div>
      </ParallaxCard>

      {/* ── White-Label chip ── */}
      <ParallaxCard
        px={chipWLX} py={chipWLY}
        style={{ top: '20px', right: '0px' }}
        floatAmt={6} floatDuration={4.5} floatDelay={0.6}
      >
        <div
          className="bg-background rounded-full px-3.5 py-2 flex items-center gap-2"
          style={{ boxShadow: 'var(--shadow-card)', border: '1px solid hsl(var(--border))' }}
        >
          <CheckCircle2 size={13} style={{ color: 'hsl(var(--apple-blue))' }} />
          <span className="text-[12px] font-semibold text-foreground">White-Label Ready</span>
        </div>
      </ParallaxCard>

      {/* ── Main talent card – Khalid ── */}
      <ParallaxCard
        px={cardKFX} py={cardKFY}
        style={{ top: '76px', right: '0px', width: '254px' }}
        floatAmt={10} floatDuration={5.2} floatDelay={0.3}
      >
        <TalentCard profile={talentProfiles[0]} />
      </ParallaxCard>

      {/* ── Second talent card – Dina ── */}
      <ParallaxCard
        px={cardDLX} py={cardDLY}
        style={{ top: '220px', left: '0px', width: '240px' }}
        floatAmt={8} floatDuration={4.8} floatDelay={1.1}
      >
        <TalentCard profile={talentProfiles[1]} />
      </ParallaxCard>

      {/* ── 40% Better Margins badge ── */}
      <ParallaxCard
        px={chipMgX} py={chipMgY}
        style={{ top: '228px', right: '8px' }}
        floatAmt={5} floatDuration={4.0} floatDelay={0.8}
      >
        <div
          className="flex items-center gap-2 bg-background rounded-full px-3 py-2"
          style={{ boxShadow: 'var(--shadow-card)', border: '1px solid hsl(var(--border))' }}
        >
          <TrendingUp size={12} style={{ color: 'hsl(142 72% 42%)' }} />
          <span className="text-[12px] font-semibold text-foreground">40% Better Margins</span>
        </div>
      </ParallaxCard>

      {/* ── Third talent card – Raj ── */}
      <ParallaxCard
        px={cardRBX} py={cardRBY}
        style={{ bottom: '20px', right: '24px', width: '248px' }}
        floatAmt={9} floatDuration={5.6} floatDelay={1.8}
      >
        <TalentCard profile={talentProfiles[2]} />
      </ParallaxCard>

      {/* ── 48h chip ── */}
      <ParallaxCard
        px={chip48hX} py={chip48hY}
        style={{ bottom: '32px', left: '16px', minWidth: '120px' }}
        floatAmt={6} floatDuration={4.2} floatDelay={1.4}
      >
        <div className="talent-card">
          <div className="text-[28px] font-black text-foreground leading-none tracking-tightest">48h</div>
          <div className="text-[11px] text-muted-foreground mt-1 font-medium">Ready to Deploy</div>
        </div>
      </ParallaxCard>

    </div>
  );
};

export const HeroSection = () => {
  return (
    <section className="min-h-screen w-full flex flex-col pt-[88px] pb-8 md:pt-[100px] md:pb-10 overflow-hidden bg-background">
      <div className="section-container flex-1 flex flex-col justify-center">
        <div className="grid lg:grid-cols-[1fr_460px] xl:grid-cols-[1fr_500px] gap-12 xl:gap-16 items-center">

          {/* Left: Copy */}
          <div className="max-w-[600px]">
            <motion.p
              className="section-label mb-6"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              The Talent Operation System for your agency
            </motion.p>

            <motion.h1
              className="text-[60px] sm:text-[72px] lg:text-[76px] xl:text-[84px] font-extrabold leading-none tracking-tightest text-foreground mb-7"
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              More Clients.<br />
              Better Margins.<br />
              <span style={{ color: 'hsl(var(--apple-grey))' }} className="font-semibold">
                No New Hires.
              </span>
            </motion.h1>

            <motion.p
              className="text-[18px] text-muted-foreground leading-[1.55] mb-9 max-w-[520px]"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
            >
              HireX8 is a Human + AI Talent Operating System - deploy white-labeled teams across design, content, SEO, engineering, and automation in 48 hours.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-3 mb-9"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.28 }}
            >
              <a
                href="https://vibe.hirex8.com/register?type=client"
                className="btn-primary"
              >
                Build Your Team Now
              </a>
              <a
                href="https://cal.com/matching-expert/30min"
                className="btn-secondary"
              >
                Book a Free Call →
              </a>
            </motion.div>
          </div>

          {/* Right: Interactive Talent Cluster */}
          <div className="hidden lg:block">
            <TalentCardCluster />
          </div>
        </div>

        {/* Agency names strip */}
        <div className="mt-10 pt-7 flex flex-wrap items-center gap-3">
          <span className="text-[12px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mr-2">
            Trusted by agencies like
          </span>
          {['Sketch To Growth', 'Phero AI', 'Peakframe Studio', 'Atlas Pixel Co.', 'Dev Studios', 'My Web 3 Startup'].map(name => (
            <span key={name} className="text-[13px] font-medium text-muted-foreground px-3 py-1.5 rounded-full border border-border">
              {name}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
